import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageCircle, Send, Sparkles } from "lucide-react";

const TEMPLATES = {
  birthday: {
    label: "Birthday",
    build: (c) => `Dear ${c.name},\n\nOn your special day, we at ${c.company_name || "STJ"} wish you a very Happy Birthday! ✨\n\nMay this year sparkle as beautifully as ${prefBits(c)}. As a small token of appreciation, we'd love to invite you to our showroom for a special preview of pieces we've picked especially for you.\n\nWarm regards.`,
  },
  anniversary: {
    label: "Anniversary",
    build: (c) => `Dear ${c.name},\n\nWishing you a joyous Anniversary! 💛\n\nCelebrating this milestone deserves a piece as timeless as your bond. ${anniversaryBits(c)}\n\nWe would be honoured to help you find the perfect one. Do stop by ${c.company_name || "STJ"} anytime — a warm cup of chai and a curated selection await.\n\nWith love.`,
  },
  festival: {
    label: "Festival Greetings",
    build: (c) => `Dear ${c.name},\n\nSeason's warmest greetings from ${c.company_name || "STJ"}! 🌼\n\nOur festive collection has just arrived — ${prefBits(c)}. Reply to this message and we'll share the catalogue directly with you, plus a special festive offer reserved for our valued clients.\n\nBest wishes.`,
  },
};

function prefBits(c) {
  const p = c.preferences || {};
  const parts = [];
  if (p.categories?.length) parts.push(`fresh ${p.categories.slice(0, 2).join(" & ").toLowerCase()} designs`);
  if (p.styles?.length) parts.push(`in ${p.styles.slice(0, 2).join(" and ")} styles`);
  if (parts.length) return parts.join(" ");
  return "our newest handcrafted pieces";
}

function anniversaryBits(c) {
  const p = c.preferences || {};
  if (p.categories?.length) {
    return `We've just added a few ${p.categories[0].toLowerCase()} pieces that would suit you beautifully.`;
  }
  return "Do take a look at our latest curation.";
}

export function WhatsAppWishDialog({ open, onOpenChange, contact, occasion, companyName, onSent }) {
  const [template, setTemplate] = useState("birthday");
  const [text, setText] = useState("");

  useEffect(() => {
    if (open && contact) {
      // pick template based on occasion label
      const label = (occasion?.label || "").toLowerCase();
      let key = "birthday";
      if (label.includes("annivers") || label.includes("wedding")) key = "anniversary";
      else if (label.includes("birth")) key = "birthday";
      else key = "festival";
      setTemplate(key);
      setText(TEMPLATES[key].build({ ...contact, company_name: companyName }));
    }
  }, [open, contact, occasion, companyName]);

  const pickTemplate = (key) => {
    setTemplate(key);
    setText(TEMPLATES[key].build({ ...contact, company_name: companyName }));
  };

  const number = useMemo(() => {
    const raw = contact?.whatsapp_number || contact?.mobile || "";
    return raw.replace(/[^0-9]/g, "");
  }, [contact]);

  const send = async () => {
    if (!number) {
      toast.error("Contact has no mobile / WhatsApp number");
      return;
    }
    const url = `https://wa.me/${number.length === 10 ? "91" + number : number}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    try {
      await api.post("/interactions", {
        contact_id: contact.id,
        type: "whatsapp",
        summary: `WhatsApp wish sent (${TEMPLATES[template].label}${occasion?.label ? ` · ${occasion.label}` : ""}): ${text.slice(0, 120)}${text.length > 120 ? "…" : ""}`,
      });
      toast.success("Wish opened in WhatsApp & logged");
      if (onSent) onSent();
      onOpenChange(false);
    } catch (_) {
      toast.warning("Wish opened, but couldn't log interaction");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="whatsapp-wish-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <MessageCircle size={20} className="text-[#25D366]"/> Send WhatsApp Wish
          </DialogTitle>
          <div className="text-sm text-[#4A5A50] mt-1">
            To {contact?.name} {occasion?.label ? `· ${occasion.label}` : ""}
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Template</Label>
            <div className="flex flex-wrap gap-2 mt-1.5">
              {Object.entries(TEMPLATES).map(([k, t]) => (
                <button
                  key={k}
                  onClick={() => pickTemplate(k)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${template === k ? "bg-[#C9A24B] text-white border-[#C9A24B]" : "bg-white text-[#0F1E15] border-[#E4DFCF] hover:bg-[#F1EDDF]"}`}
                  data-testid={`wish-template-${k}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-[#4A5A50]">Message (editable)</Label>
            <Textarea
              rows={10}
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="mt-1.5 font-sans text-sm"
              data-testid="wish-message"
            />
            <div className="text-[10px] text-[#4A5A50] mt-1 flex items-center gap-1">
              <Sparkles size={10} className="text-[#C9A24B]"/> Personalized using {contact?.name}'s captured preferences.
            </div>
          </div>

          {!number && (
            <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2">
              This contact has no mobile number — please add one first.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={send}
            disabled={!number}
            className="bg-[#25D366] hover:bg-[#1EBE5B] text-white rounded-full"
            data-testid="wish-send-btn"
          >
            <Send size={14} className="mr-1.5"/> Open WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
